import { z } from 'zod'
import { DayType } from '../types/enums'

/**
 * Tek timesheet satırı — aynı günde birden fazla proje için kullanılır.
 * [IK-02] Yalnızca atandığı aktif projelere giriş yapılabilir.
 */
const TimesheetLineSchema = z.object({
  projectId: z.string({ required_error: 'Proje seçimi zorunludur.' }).uuid(),
  taskId: z.string().uuid().nullable().optional(),
  progressItemId: z.string().uuid().nullable().optional(),
  /** Normal çalışma saati */
  hours: z
    .string({ required_error: 'Saat zorunludur.' })
    .regex(/^\d+$/, 'Saat tam sayı olmalıdır.')
    .refine((val) => parseInt(val) >= 1, 'Minimum giriş süresi 1 saattir.')
    .refine((val) => parseInt(val) <= 16, 'Tek kayıtta en fazla 16 saat girilebilir.'),
  /** Mesai saati — isteğe bağlı, maliyete 1.5x çarpanla yansır */
  overtimeHours: z
    .string()
    .regex(/^\d+(\.\d{1,2})?$/, 'Geçerli bir mesai saati giriniz.')
    .refine((val) => parseFloat(val) >= 0, 'Mesai saati negatif olamaz.')
    .optional()
    .default('0'),
  description: z
    .string({ required_error: 'Açıklama zorunludur.' })
    .min(10, 'Açıklama en az 10 karakter olmalıdır. (Ne yaptığınızı belirtin.)')
    .max(500),
})

/**
 * Günlük batch giriş — bir günde birden fazla proje satırı tek request ile gönderilir.
 * [IK-08] Geçmişe dönük giriş tarih kontrolü backend'de yapılır.
 */
export const BatchTimesheetSchema = z.object({
  date: z
    .string({ required_error: 'Tarih zorunludur.' })
    .date('Geçerli bir tarih giriniz. (YYYY-MM-DD)'),
  dayType: z.nativeEnum(DayType).default(DayType.WORK),
  /** İzin günlerinde entries boş olabilir */
  entries: z
    .array(TimesheetLineSchema)
    .max(20, 'Aynı günde en fazla 20 satır girilebilir.'),
  /**
   * Gecikmeli giriş nedeni — [IK-08] 4+ gün gecikmede zorunlu.
   * Backend tarih farkını hesaplar ve gerekirse bu alanı zorunlu tutar.
   */
  delayReason: z.string().min(10).max(500).nullable().optional(),
})

export const UpdateTimesheetEntrySchema = z.object({
  hours: z
    .string()
    .regex(/^\d+$/, 'Saat tam sayı olmalıdır.')
    .refine((val) => parseInt(val) >= 1, 'Minimum 1 saat.')
    .refine((val) => parseInt(val) <= 16, 'Maksimum 16 saat.')
    .optional(),
  description: z.string().min(10).max(500).optional(),
  taskId: z.string().uuid().nullable().optional(),
  progressItemId: z.string().uuid().nullable().optional(),
})

export const ApproveTimesheetSchema = z.object({
  entryIds: z
    .array(z.string().uuid())
    .min(1, 'En az bir timesheet kaydı seçiniz.'),
})

export const RejectTimesheetSchema = z.object({
  entryIds: z
    .array(z.string().uuid())
    .min(1, 'En az bir timesheet kaydı seçiniz.'),
  reason: z
    .string({ required_error: 'Red nedeni zorunludur.' })
    .min(10, 'Red nedeni en az 10 karakter olmalıdır.')
    .max(500),
})

/**
 * Aylık kapanış — [IK-03] Kapanış sonrası kayıtlar değiştirilemez.
 */
export const MonthlyClosingSchema = z.object({
  projectId: z.string({ required_error: 'Proje zorunludur.' }).uuid(),
  year: z
    .number({ required_error: 'Yıl zorunludur.' })
    .int()
    .min(2020)
    .max(2100),
  month: z
    .number({ required_error: 'Ay zorunludur.' })
    .int()
    .min(1)
    .max(12),
})

export type BatchTimesheetInput = z.infer<typeof BatchTimesheetSchema>
export type UpdateTimesheetEntryInput = z.infer<typeof UpdateTimesheetEntrySchema>
export type ApproveTimesheetInput = z.infer<typeof ApproveTimesheetSchema>
export type RejectTimesheetInput = z.infer<typeof RejectTimesheetSchema>
export type MonthlyClosingInput = z.infer<typeof MonthlyClosingSchema>
